import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  RudderstackIcon,
} from '@tinct/web-components';
import { RudderstackCredentials } from '@tinct/core-api-models';
import { createConnectedInputHelpers } from '@tinct/web-form';
import { FormProvider } from 'react-hook-form';
import { X } from 'lucide-react';
import { useRudderstackConfigForm } from '../hooks/use-rudderstack-config-form';

type RudderstackConfigModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  integrationId?: string;
};

export const RudderstackConfigModal = ({
  open,
  onOpenChange,
  organizationId,
  integrationId,
}: RudderstackConfigModalProps) => {
  const { form, handleSubmit, isEditing, isLoading, reset } =
    useRudderstackConfigForm({
      organizationId,
      integrationId,
      onSuccess: () => onOpenChange(false),
    });

  const { ControlledTextInput } =
    createConnectedInputHelpers<RudderstackCredentials>();

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-6 rounded-3xl p-8 shadow-lg sm:max-w-md"
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <RudderstackIcon className="size-10 shrink-0" />
          <div className="flex flex-1 flex-col gap-1.5">
            <DialogTitle className="font-semibold leading-none tracking-tight text-foreground">
              Configure RudderStack
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {isEditing
                ? 'Update your write key and data plane URL. Leave the write key unchanged to keep the current one.'
                : 'Connect RudderStack with your write key and data plane URL.'}
            </p>
          </div>
          <DialogClose className="shrink-0 opacity-70 transition-opacity hover:opacity-100">
            <X className="size-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>

        <FormProvider {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 rounded-2xl border p-6">
              <ControlledTextInput
                name="writeKey"
                label="Write Key *"
                placeholder="Your RudderStack write key"
                direction="vertical"
              />
              <ControlledTextInput
                name="dataPlaneUrl"
                label="Data Plane URL *"
                placeholder="https://your-dataplane.rudderstack.com"
                direction="vertical"
              />
            </div>

            {form.formState.errors.root && (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}

            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                {isEditing ? 'Save' : 'Save & Connect'}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
